import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  BreathingTubePreferencesInfoRec,
  breathingTubePreferencesService,
} from '../../../../services/breathingTubePreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BreathingTubePreferencesData');

type RouteParams = {
  breathingTubePreferencesId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type BreathingTubePreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'BreathingTubePreferencesData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const BreathingTubePreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BreathingTubePreferencesDataScreenRouteProp>();
  const [breathingTubePreferencesInfo, setBreathingTubePreferencesInfo] = useState<BreathingTubePreferencesInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const breathingTubePreferencesId = route.params?.breathingTubePreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadBreathingTubePreferencesInfo();
  }, []);

  const loadBreathingTubePreferencesInfo = async () => {
    try {
      if (breathingTubePreferencesId) {
        const info = await breathingTubePreferencesService.getBreathingTubePreferencesById(breathingTubePreferencesId, targetUserId);
        setBreathingTubePreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await breathingTubePreferencesService.getAllBreathingTubePreferences(targetUserId);
        if (allInfo.length > 0) {
          setBreathingTubePreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading breathing tube preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (breathingTubePreferencesInfo) {
      // Map the data from breathingTubePreferencesInfo to the format expected by BreathingTubePreferencesInputScreen
      const mappedData = {
        id: breathingTubePreferencesInfo.id,
        breathingTubeType: breathingTubePreferencesInfo.breathingTubeType,
        locationOfDirective: breathingTubePreferencesInfo.locationOfDirective,
        specialInstructions: breathingTubePreferencesInfo.specialInstructions,
        breathingTubeDetails: breathingTubePreferencesInfo.breathingTubeDetails,
        notes: breathingTubePreferencesInfo.notes,
      };

      navigation.navigate('BreathingTubePreferencesInput', {
        breathingTubePreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('BreathingTubePreferencesInput');
    }
  };

  const handleDelete = () => {
    if (breathingTubePreferencesInfo) {
      Alert.alert(
        'Delete Breathing Tube Preferences',
        'Are you sure you want to delete these breathing tube preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await breathingTubePreferencesService.deleteBreathingTubePreferences(breathingTubePreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await breathingTubePreferencesService.getAllBreathingTubePreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('breathing_tube_preferences');
                }

                (navigation as any).navigate('FinalCare', { screen: 'FinalCareChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting breathing tube preferences info:', error);
                Alert.alert('Error', 'Failed to delete breathing tube preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('FinalCare', { screen: 'FinalCareChecklist', params: { refreshKey: Date.now() } });
  };

  const formatBreathingTubeType = (breathingTubeType?: string): string => {
    if (!breathingTubeType) return 'Not specified';

    const typeMapping: Record<string, string> = {
      'full_breathing_tube': 'Full Decline of Breathing Tube',
      'limited': 'Limited Breathing Assistance',
      'specific': 'Specific Medical Situations',
      'natural': 'Natural Breathing Preferences'
    };

    return typeMapping[breathingTubeType] || breathingTubeType;
  };

  const formatBreathingTubeDetails = (breathingTubeDetails: any, breathingTubeType?: string): string => {
    if (!breathingTubeDetails || typeof breathingTubeDetails !== 'object') {
      return 'Not specified';
    }

    const details: string[] = [];

    if (breathingTubeType === 'full_breathing_tube') {
      if (breathingTubeDetails.specificInterventionsToDecline) {
        details.push(`Interventions to Decline: ${breathingTubeDetails.specificInterventionsToDecline}`);
      }
      if (breathingTubeDetails.discussedWithHealthcareProvider !== undefined) {
        details.push(`Discussed with Healthcare Provider: ${breathingTubeDetails.discussedWithHealthcareProvider ? 'Yes' : 'No'}`);
      }
      if (breathingTubeDetails.conditionsForBreathingTube) {
        details.push(`Conditions for Breathing Tube: ${breathingTubeDetails.conditionsForBreathingTube}`);
      }
      if (breathingTubeDetails.additionalInstructions) {
        details.push(`Additional Instructions: ${breathingTubeDetails.additionalInstructions}`);
      }
    } else if (breathingTubeType === 'limited') {
      if (breathingTubeDetails.acceptableInterventions) {
        details.push(`Acceptable Interventions: ${breathingTubeDetails.acceptableInterventions}`);
      }
      if (breathingTubeDetails.limitedBreathingTubeConditions) {
        details.push(`Limited Breathing Tube Conditions: ${breathingTubeDetails.limitedBreathingTubeConditions}`);
      }
      if (breathingTubeDetails.medicationPreferences) {
        details.push(`Medication Preferences: ${breathingTubeDetails.medicationPreferences}`);
      }
      if (breathingTubeDetails.timeLimits) {
        details.push(`Time Limits: ${breathingTubeDetails.timeLimits}`);
      }
    } else if (breathingTubeType === 'specific') {
      if (breathingTubeDetails.triggeringConditions) {
        details.push(`Triggering Conditions: ${breathingTubeDetails.triggeringConditions}`);
      }
      if (breathingTubeDetails.exceptions) {
        details.push(`Exceptions: ${breathingTubeDetails.exceptions}`);
      }
      if (breathingTubeDetails.treatmentPreferences) {
        details.push(`Treatment Preferences: ${breathingTubeDetails.treatmentPreferences}`);
      }
      if (breathingTubeDetails.discussedWithDoctor !== undefined) {
        details.push(`Discussed with Doctor: ${breathingTubeDetails.discussedWithDoctor ? 'Yes' : 'No'}`);
      }
    } else if (breathingTubeType === 'natural') {
      if (breathingTubeDetails.comfortCarePreferences) {
        details.push(`Comfort Care Preferences: ${breathingTubeDetails.comfortCarePreferences}`);
      }
      if (breathingTubeDetails.painManagementOptions) {
        details.push(`Pain Management Options: ${breathingTubeDetails.painManagementOptions}`);
      }
      if (breathingTubeDetails.spiritualCulturalPractices) {
        details.push(`Spiritual/Cultural Practices: ${breathingTubeDetails.spiritualCulturalPractices}`);
      }
      if (breathingTubeDetails.environmentPreferences) {
        details.push(`Environment Preferences: ${breathingTubeDetails.environmentPreferences}`);
      }
    }

    return details.length > 0 ? details.join('\n\n') : 'Not specified';
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Breathing Tube Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!breathingTubePreferencesInfo) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Breathing Tube Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>No breathing tube preferences found</Text>
            <TouchableOpacity
              style={[styles.button, styles.editButton, { marginTop: 20 }]}
              onPress={handleEdit}
            >
              <Text style={styles.buttonText}>Add Breathing Tube Preferences</Text>
            </TouchableOpacity>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleBack}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Breathing Tube Preferences Details</Text>

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          <InfoItem
            label="Breathing Tube Preference Type"
            value={formatBreathingTubeType(breathingTubePreferencesInfo?.breathingTubeType)}
          />
          <InfoItem
            label="Location of Directive"
            value={breathingTubePreferencesInfo?.locationOfDirective}
          />
          <InfoItem
            label="Special Instructions"
            value={breathingTubePreferencesInfo?.specialInstructions}
          />
          <InfoItem
            label="Breathing Tube Details"
            value={formatBreathingTubeDetails(breathingTubePreferencesInfo?.breathingTubeDetails, breathingTubePreferencesInfo?.breathingTubeType)}
          />
          <InfoItem
            label="Notes"
            value={breathingTubePreferencesInfo?.notes}
          />

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={handleBack}
            >
              <Text style={styles.buttonText}>Back</Text>
            </TouchableOpacity>

            {!viewOnly && (
              <>
                <TouchableOpacity
                  style={[styles.button, styles.deleteButton]}
                  onPress={handleDelete}
                >
                  <Text style={styles.buttonText}>Delete</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.button, styles.editButton]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(15),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF6B6B',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
